import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import DimpleDivider from '@tds/core-dimple-divider';
import FileDownload from 'js-file-download';
import Button from '@tds/core-button';
import Heading from '@tds/core-heading';
import Spinner from '@tds/core-spinner';
import queryString from 'query-string';
import { useHistory } from 'react-router';
import ValidateLoader from '../UI/validateLoader';
import { CONSTANT } from '../utils/constant';
import request, {
  defectManagementConfig,
  getEnvironment
} from '../utils/request';
import Filters from './Filters';
import { DEFECTS_PER_PAGE, FILTERS_INIT_STATE } from './constants';
import { Box, ContentWrapper, TopbarWrapper } from '../design/css';
import { Container, TableBottomBar } from './css';
import { useLocaleContext } from '../context/LocaleContext';
import EN from '../../EN.json';
import FR from '../../FR.json';
import Pagination from './Pagination';
import Table from './Table';
import { TableWrapper } from '../design/shared/table/css';
import {
  useFiltersActionsContext,
  useFiltersContext
} from '../context/FiltersContext';
import { getFormattedDate } from '../utils/utils';

const DefectManagement = () => {
  const locale = useLocaleContext();
  const historyRef = useHistory();

  const search = window.location.search;
  let { defectId } = queryString.parse(search) || null;

  const filterLastStatus = useFiltersContext();
  const {
    setFilters: setFilterLastStatus,
    areFiltersTouched
  } = useFiltersActionsContext();

  const {
    DEFECT_MANAGEMENT: {
      DEFECT_TABLE = {},
      DEFECTS = '',
      NO_DEFECTS = '',
      Export_Defects
    } = {}
  } = locale === 'en-US' ? EN : FR;

  const {
    KEYS: {
      ROLES: { ADMIN }
    }
  } = CONSTANT;

  const { getProfile } = request;
  const profile = getProfile();
  const { id: userId, role, email: userEmail } = profile;
  // States
  const [defects, setDefects] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [totalDefects, setTotalDefects] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [fetchData, setFetchData] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [isExportingDefects, setExportingDefects] = useState(false);

  const currentUserAssigneeEmail = useMemo(() => {
    if (role.name === ADMIN && assignedUsers.length) {
      const { email: foundEmail } = assignedUsers.find(
        ({ email }) => email === userEmail
      );
      return foundEmail;
    }
    return null;
  }, [assignedUsers]);

  // API Call to Fetch all Defects
  function fetchAllDefects(query = {}) {
    setPageLoading(true);
    const { startDate, endDate } = query;
    const formettedStartDate = startDate ? getFormattedDate(startDate) : null;
    const formattedEndDate = endDate ? getFormattedDate(endDate) : null;
    const params = {
      ...query,
      startDate: formettedStartDate,
      endDate: formattedEndDate
    };
    const { DEFECTS_ACCESS_TOKEN, apiHost } = getEnvironment();
    if (role.name === ADMIN) {
      const url = `${apiHost}/defects/getall/${pageNumber}/${DEFECTS_PER_PAGE}`;
      const config = defectManagementConfig('get', url);
      setPageLoading(true);
      axios({ ...config, params: params }).then(res => {
        const {
          data: { totalCount = 0, status = 1, message = '', innerData = [] }
        } = res;
        if (status === 1 && message) {
          setDefects(innerData);
          setTotalDefects(totalCount);
          setFetchData(false);
          setPageLoading(false);
        }
      });
      const adminUrl = `${apiHost}/defects/getadmins`;
      const adminConfig = defectManagementConfig('get', adminUrl);
      axios({ ...adminConfig }).then(res => {
        const {
          data: { adminList = [] }
        } = res;
        setAssignedUsers(adminList);
      });
    } else {
      const defectUrl = `${apiHost}/defects/getdefect/${userId}`;
      const userConfig = defectManagementConfig('get', defectUrl);
      axios({
        ...userConfig,
        params: params,
        headers: {
          access_token: DEFECTS_ACCESS_TOKEN,
          'Access-Control-Allow-Origin': '*'
        }
      }).then(res => {
        const {
          data: { totalCount = 0, status = 1, message = '', innerData = [] }
        } = res;
        if (status === 1 && message) {
          setDefects(innerData);
          setTotalDefects(totalCount);
          setFetchData(false);
          setPageLoading(false);
        }
      });
    }
  }

  useEffect(() => {
    if (pageNumber || fetchData) {
      if (filterLastStatus || currentUserAssigneeEmail) {
        applyFilter({
          ...filterLastStatus,
          ...(currentUserAssigneeEmail && !areFiltersTouched
            ? { assignee: currentUserAssigneeEmail }
            : {})
        });
      } else fetchAllDefects();
    }
  }, [pageNumber, fetchData, assignedUsers.length]);

  // filter data from child to parent
  const applyFilter = filter => {
    setFilterLastStatus(filter, {
      shouldUpdateTouchedStatus: !!currentUserAssigneeEmail
    });
    Object.keys(FILTERS_INIT_STATE).forEach(key =>
      // eslint-disable-next-line no-param-reassign
      filter[key] === '' ? delete filter[key] : filter[key]
    );
    fetchAllDefects(filter);
  };

  const reset = () => {
    setFilterLastStatus({});
    fetchAllDefects();
  };

  const onExportClick = () => {
    const { apiHost } = getEnvironment();
    setExportingDefects(true);
    const filter = filterLastStatus;
    Object.keys(FILTERS_INIT_STATE).forEach(key =>
      // eslint-disable-next-line no-param-reassign
      filter[key] === '' ? delete filter[key] : filter[key]
    );
    const url = `${apiHost}/defects/export-defects`;
    const config = defectManagementConfig('post', url);
    axios
      .post(
        url,
        { language: locale, role: role.name },
        { ...config, responseType: 'arraybuffer', params: filter }
      )
      .then(response => response.data)
      .then(fileData => {
        FileDownload(fileData, 'defects.csv');
        setExportingDefects(false);
      })
      .catch(() => {});
  };

  return (
    <Container>
      <ContentWrapper style={{ padding: '0 4rem' }}>
        <Filters
          onApplyFilters={applyFilter}
          onResetFilters={reset}
          role={role}
          assignedUsers={assignedUsers}
          defaultFilters={{
            ...filterLastStatus,
            ...(currentUserAssigneeEmail && !areFiltersTouched
              ? { assignee: currentUserAssigneeEmail }
              : {})
          }}
        />
        <DimpleDivider />
        {pageLoading && <ValidateLoader message="Loading" />}
        {!pageLoading && !totalDefects && (
          <Heading level="h3"> {NO_DEFECTS}</Heading>
        )}
        {!pageLoading && totalDefects > 0 && (
          <Box
            style={{
              border: '1px solid #e3e3e3'
            }}
          >
            <TopbarWrapper style={{ color: '#fff', fontWeight: '900' }}>
              {`${DEFECTS} (${totalDefects})`}
            </TopbarWrapper>
            <TableWrapper>
              <Table
                setFetchData={setFetchData}
                header={DEFECT_TABLE}
                data={defects}
                userProfile={profile}
                defectId={defectId}
                onDefectSelected={val => {
                  if (val && val.id) {
                    historyRef.push({
                      search: `?defectId=${val.id}`
                    });
                  } else {
                    historyRef.push({
                      search: ''
                    });
                    // historyRef.push({
                    //   pathname: location.pathname
                    // });
                    defectId = null;
                    if (filterLastStatus) {
                      applyFilter(filterLastStatus);
                    }
                  }
                }}
              />
            </TableWrapper>
          </Box>
        )}
        <Pagination
          pageNumber={pageNumber}
          totalDefects={totalDefects}
          defectsPerPage={DEFECTS_PER_PAGE}
          setPageNumber={setPageNumber}
        />
        {role && role.name === ADMIN ? (
          <TableBottomBar>
            <Spinner
              spinning={isExportingDefects}
              size="small"
              label="Request is processing."
              inline
            >
              <Button onClick={onExportClick}>{Export_Defects}</Button>
            </Spinner>
          </TableBottomBar>
        ) : (
          ''
        )}
      </ContentWrapper>
    </Container>
  );
};

export default DefectManagement;
